// lingland-frontend/src/pages/ServicesPage.js
/* eslint-disable no-unused-vars */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import SEO from '../components/SEO';
import { FileText, MonitorSmartphone, MicVocal, Edit3, Globe, SearchCheck, LayoutTemplate, Building2, HeartPulse, Scale, Banknote, ShoppingCart, Megaphone, Factory, Check, Star, ShieldCheck, Users } from 'lucide-react';
import './ServicesPage.css';

const ServiceEntry = ({ icon: Icon, title, description, benefits, imagePosition = 'left', isVisible }) => (
    <div className={`service-entry ${imagePosition === 'right' ? 'icon-right' : ''} animate-on-scroll ${isVisible ? 'is-visible' : ''}`}>
        <div className="service-icon">
            <Icon size={64} strokeWidth={1.5} />
        </div>
        <div className="service-text">
            <h3>{title}</h3>
            <p>{description}</p>
            {benefits && benefits.length > 0 && (
                <ul className="service-benefits">
                    {benefits.map((benefit, index) => (
                        <li key={index}>
                            <Check size={18} className="benefit-icon" />
                            <span>{benefit}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </div>
);

function ServicesPage() {
    const { t } = useTranslation();
    const [titleRef, isTitleVisible] = useIntersectionObserver();
    const [service1Ref, isService1Visible] = useIntersectionObserver();
    const [service2Ref, isService2Visible] = useIntersectionObserver();
    const [service3Ref, isService3Visible] = useIntersectionObserver();
    const [service4Ref, isService4Visible] = useIntersectionObserver();
    const [service5Ref, isService5Visible] = useIntersectionObserver();
    const [service6Ref, isService6Visible] = useIntersectionObserver();
    const [industriesRef, isIndustriesVisible] = useIntersectionObserver();
    const [qualityRef, isQualityVisible] = useIntersectionObserver();
    const [ctaEndRef, isCtaEndVisible] = useIntersectionObserver();

    return (
        <>
            <SEO
                title={t('services_header_title')}
                description={t('services_header_subtitle')}
                url="/uslugi"
            />
            <section ref={titleRef} className={`page-header-section section-bg-white animate-on-scroll ${isTitleVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h1>{t('services_header_title')}</h1>
                    <p className="subtitle">{t('services_header_subtitle')}</p>
                </div>
            </section>

            <section ref={service1Ref} className="service-section section-bg-light-gray">
                <div className="container">
                    <ServiceEntry
                        icon={FileText}
                        title={t('services_entry1_title')}
                        description={t('services_entry1_desc')}
                        benefits={[
                            t('services_entry1_benefit1'),
                            t('services_entry1_benefit2'),
                            t('services_entry1_benefit3'),
                            t('services_entry1_benefit4')
                        ]}
                        isVisible={isService1Visible}
                        imagePosition="left"
                    />
                </div>
            </section>

            <section ref={service2Ref} className="service-section section-bg-white">
                 <div className="container">
                    <ServiceEntry
                        icon={MicVocal}
                        title={t('services_entry2_title')}
                        description={t('services_entry2_desc')}
                        benefits={[
                            t('services_entry2_benefit1'),
                            t('services_entry2_benefit2'),
                            t('services_entry2_benefit3'),
                            t('services_entry2_benefit4')
                        ]}
                        isVisible={isService2Visible}
                        imagePosition="right"
                    />
                </div>
            </section>

             <section ref={service3Ref} className="service-section section-bg-light-gray">
                 <div className="container">
                    <ServiceEntry
                        icon={MonitorSmartphone}
                        title={t('services_entry3_title')}
                        description={t('services_entry3_desc')}
                        benefits={[
                            t('services_entry3_benefit1'),
                            t('services_entry3_benefit2'),
                            t('services_entry3_benefit3'),
                            t('services_entry3_benefit4')
                        ]}
                        isVisible={isService3Visible}
                        imagePosition="left"
                    />
                </div>
            </section>

            <section ref={service4Ref} className="service-section section-bg-white">
                 <div className="container">
                    <ServiceEntry
                        icon={Edit3}
                        title={t('services_entry4_title')}
                        description={t('services_entry4_desc')}
                        benefits={[
                            t('services_entry4_benefit1'),
                            t('services_entry4_benefit2'),
                            t('services_entry4_benefit3'),
                            t('services_entry4_benefit4')
                        ]}
                        isVisible={isService4Visible}
                        imagePosition="right"
                    />
                </div>
            </section>

            <section ref={service5Ref} className="service-section section-bg-light-gray">
                 <div className="container">
                    <ServiceEntry
                        icon={LayoutTemplate}
                        title={t('services_entry5_title')}
                        description={t('services_entry5_desc')}
                        benefits={[
                            t('services_entry5_benefit1'),
                            t('services_entry5_benefit2'),
                            t('services_entry5_benefit3'),
                            t('services_entry5_benefit4')
                        ]}
                        isVisible={isService5Visible}
                        imagePosition="left"
                    />
                </div>
            </section>

            <section ref={service6Ref} className="service-section section-bg-white">
                 <div className="container">
                    <ServiceEntry
                        icon={SearchCheck}
                        title={t('services_entry6_title')}
                        description={t('services_entry6_desc')}
                        benefits={[
                            t('services_entry6_benefit1'),
                            t('services_entry6_benefit2'),
                            t('services_entry6_benefit3'),
                            t('services_entry6_benefit4')
                        ]}
                        isVisible={isService6Visible}
                        imagePosition="right"
                    />
                </div>
            </section>

             <section ref={industriesRef} className={`industries-section section-bg-light-gray animate-on-scroll ${isIndustriesVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h2>{t('services_industries_title')}</h2>
                    <p className="subtitle">{t('services_industries_subtitle')}</p>
                    <div className="industries-grid">
                        <div className="industry-item"><MonitorSmartphone size={32} strokeWidth={1.5}/><span>{t('industry_it')}</span></div>
                        <div className="industry-item"><Scale size={32} strokeWidth={1.5}/><span>{t('industry_legal')}</span></div>
                        <div className="industry-item"><HeartPulse size={32} strokeWidth={1.5}/><span>{t('industry_medical')}</span></div>
                        <div className="industry-item"><Megaphone size={32} strokeWidth={1.5}/><span>{t('industry_marketing')}</span></div>
                        <div className="industry-item"><ShoppingCart size={32} strokeWidth={1.5}/><span>{t('industry_ecommerce')}</span></div>
                        <div className="industry-item"><Factory size={32} strokeWidth={1.5}/><span>{t('industry_industrial')}</span></div>
                    </div>
                </div>
            </section>

             <section ref={qualityRef} className={`quality-section section-bg-white animate-on-scroll ${isQualityVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h2>{t('services_quality_title')}</h2>
                    <p className="subtitle">{t('services_quality_subtitle')}</p>
                    <div className="quality-steps-grid">
                        <div className="quality-step">
                            <Star size={40} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('services_quality_step1_title')}</h4>
                            <p>{t('services_quality_step1_desc')}</p>
                        </div>
                         <div className="quality-step">
                            <Edit3 size={40} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('services_quality_step2_title')}</h4>
                            <p>{t('services_quality_step2_desc')}</p>
                        </div>
                        <div className="quality-step">
                            <ShieldCheck size={40} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('services_quality_step3_title')}</h4>
                            <p>{t('services_quality_step3_desc')}</p>
                        </div>
                        <div className="quality-step">
                            <Users size={40} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('services_quality_step4_title')}</h4>
                            <p>{t('services_quality_step4_desc')}</p>
                        </div>
                    </div>
                </div>
            </section>

             <section className={`cta-services-end section-bg-accent animate-on-scroll ${isCtaEndVisible ? 'is-visible' : ''}`} ref={ctaEndRef}>
                 <div className="container text-center">
                    <h2>{t('services_cta_title')}</h2>
                    <p className="subtitle">{t('services_cta_subtitle')}</p>
                    <Link to="/kontakt" className="button-inverted">
                       {t('services_cta_button')}
                    </Link>
                 </div>
            </section>
        </>
    );
}

export default ServicesPage;